import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { OfferStatus, UserData } from 'pages/offer/types';
import { formatDate } from 'shared/lib';
import { getToken, keycloak } from 'shared/keycloak';
import { sendOfferDecision, getOfferData } from '../api';
import { defaultUser } from './consts';
import { TransformBackDataToFrontData } from './transform-back-data-to-front-data';

type StepType = 'initial' | 'chooseDate' | 'accepted' | 'reject' | 'rejected';

export type OfferPageContextData = {
  isLoading: boolean;
  isError: boolean;
  userData: UserData;
  accept: () => void;
  reject: () => void;
  closeModal: () => void;
  plan: () => void;
  confirmReject: () => void;
  step: StepType;
  isModalOpened: boolean;
  formattedSelectedDate: string;
  isErrorDate: boolean;
  selectDate: (date: Date | null) => void;
  selectedDate: Date | null;
  errorText: string;
};

export const OfferPageContext = createContext<OfferPageContextData | null>(null);

export const useOfferPageContext = () => {
  const data = useContext(OfferPageContext);

  if (!data) {
    throw new Error();
  }

  return data;
};

export const OfferPageContextProvider = ({ children }: PropsWithChildren) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserData>(TransformBackDataToFrontData(defaultUser));

  const [step, setStep] = useState<StepType>('initial');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isErrorDate, setIsErrorDate] = useState(false);
  const { status, startWorkDate } = userData;
  const isModalOpened = step === 'chooseDate' || step === 'reject';
  const formattedSelectedDate = formatDate(selectedDate?.toString() || '');
  const [authToken, setAuthToken] = useState('');
  const [errorText, setErrorText] = useState('');
  const { id: offerId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get('token');

  useEffect(() => {
    if (queryToken) {
      setAuthToken(`Token ${queryToken}`);
    } else {
      setIsLoading(false);
    }
  }, [queryToken]);

  const handleAccept = () => {
    setStep('chooseDate');
    document.body.style.overflowY = 'hidden';
  };
  const handleReject = () => {
    setStep('reject');
    document.body.style.overflowY = 'hidden';
  };

  const clear = () => {
    setIsErrorDate(false);
    setStep('initial');
    document.body.style.overflowY = 'scroll';
  };

  const handleCloseModal = () => {
    setSelectedDate(null);
    clear();
  };

  const handlePlan = async () => {
    if (!selectedDate) {
      setIsErrorDate(true);
      return;
    }

    if (!authToken) {
      setIsError(true);
      return;
    }

    try {
      await sendOfferDecision({
        status: OfferStatus.accepted,
        startWorkDate: `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`,
        token: authToken,
        id: offerId,
      });

      clear();
      setStep('accepted');
    } catch (e) {
      console.error(e);
      setErrorText(typeof e === 'string' ? e : (e as any)?.detail);
      setIsError(true);
    }
  };

  const handleConfirmReject = async () => {
    if (!authToken) {
      setIsError(true);
      return;
    }

    try {
      await sendOfferDecision({
        status: OfferStatus.refused,
        token: authToken,
        id: offerId,
      });

      clear();
      setStep('rejected');
    } catch (e) {
      console.error(e);
      setErrorText(typeof e === 'string' ? e : (e as any)?.detail);
      setIsError(true);
    }
  };

  const handleSelectDate = (date: Date | null) => {
    setSelectedDate(date);
  };

  useEffect(() => {
    (async () => {
      if (!keycloak?.authenticated) return;
      const token = await getToken();
      if (!token) return;

      setAuthToken(`Bearer ${token}`);
    })();
  }, []);

  useEffect(() => {
    if (!authToken) return;
    if (userData.id || !offerId) {
      setIsLoading(false);
      return;
    }

    (async () => {
      try {
        setIsLoading(true);

        const data = await getOfferData(authToken, offerId);
        const formattedData = TransformBackDataToFrontData(data);

        setUserData(formattedData);
      } catch (e) {
        console.error(e);
        setErrorText(typeof e === 'string' ? e : (e as any)?.detail);
        setIsError(true);
      }

      setIsLoading(false);
    })();
  }, [authToken, userData.id, offerId]);

  useEffect(() => {
    if (status === OfferStatus.accepted) {
      setStep('accepted');
      setSelectedDate(new Date(startWorkDate));
    }

    if (status === OfferStatus.refused) {
      setStep('rejected');
    }
  }, [status, startWorkDate]);

  return (
    <OfferPageContext.Provider
      value={{
        isLoading: isLoading,
        isError: isError,
        userData: userData,
        accept: handleAccept,
        reject: handleReject,
        closeModal: handleCloseModal,
        plan: handlePlan,
        confirmReject: handleConfirmReject,
        step: step,
        formattedSelectedDate: formattedSelectedDate,
        isErrorDate: isErrorDate,
        isModalOpened: isModalOpened,
        selectDate: handleSelectDate,
        selectedDate: selectedDate,
        errorText,
      }}
    >
      {children}
    </OfferPageContext.Provider>
  );
};
